import { Fragment, memo, useMemo } from "react";
import TextCell from "./cells/TextCell";
import TagValueCell from "./cells/TagValueCell";
import ModuleTagCell from "./cells/ModuleTagCell";
import MultiPickCell from "./cells/MultiPickCell";
import ListCell from "./cells/ListCell";
import SelectCell from "./cells/SelectCell";
import EmissionFactorsCell from "./cells/EmissionFactorsCell";
import UnitsCell from "./cells/UnitsCell";
import TagGroupsCell from "./cells/TagGroupsCell";
import ChipsCell from "./cells/ChipsCell";
import SwitchCell from "./cells/SwitchCell";
import TableSetupCell from "./cells/TableSetupCell";
import VariableUnitsCell from "./cells/VariableUnitsCell";
import { isKnownKpiType, kpiTypeOptionsFor } from "../model/helpers";
import { isModuleKeyword, normalizeTagText } from "../../../../_constants/esgPillars";

const EMPTY = [];

const tagValue = (groups, groupName, index) =>
  (groups || []).find((group) => group.name === groupName)?.pairs[index]?.value ?? "";

/**
 * One KPI as a row of live cells.
 *
 * The row is handed the saved row and its patch — the object of unsaved edits
 * — and displays their overlay. Because the patch holds only fields that
 * actually differ, a cell is dirty precisely when the field is a key of the
 * patch: no comparison happens here.
 *
 * Memoised on stable props. Only the row whose patch, selection, error text or
 * factor entry changed re-renders, so a keystroke does not repaint three
 * hundred rows.
 *
 * @param {object} props
 * @param {{ key: string }[]} props.columns visible columns, in order
 * @param {object} props.row saved row, or a draft that has never been saved
 * @param {object} [props.patch] unsaved edits for this row
 * @param {boolean} props.isNew
 * @param {boolean} props.selected
 * @param {string} [props.titleError]
 * @param {string} [props.moduleError]
 * @param {string} [props.aggregationError]
 * @param {string} [props.unitsError]
 * @param {string} [props.factorsError]
 * @param {string} [props.detailsError] a new tabular KPI with no table
 * @param {boolean} props.aggregationDisabled
 * @param {boolean} [props.factorsLoading]
 * @param {boolean} [props.factorsFailed] factors did not load, so the cell is locked
 * @param {() => void} [props.onRetryFactors]
 * @param {{ value: string, label: string }[]} props.scopeOptions
 * @param {string[]} props.countrySuggestions
 * @param {object[]} props.ghgDatabases
 * @param {{ value: string, label: string }[]} props.moduleOptions
 * @param {string} props.financialYearLabel the row's year, which the grid shows but does not edit
 * @param {{ value: string, label: string }[]} props.typeOptions
 * @param {{ value: string, label: string }[]} props.frequencyOptions
 * @param {{ value: string, label: string }[]} props.aggregationOptions
 * @param {object[]} props.unitMaster
 * @param {string[]} props.tagKeys
 * @param {string[]} props.fixedTagGroups group names drawn as their own columns, not in Other tags
 * @param {Record<string, string[]>} props.tagOptions values paired with each tag key, by key
 * @param {Record<string, string[]>} props.tagValuesByKey
 * @param {Record<string, string>} props.modulePillars ESG pillar by normalized module tag
 * @param {string[]} props.evidenceCatalogue
 * @param {{ value: string, label: string }[]} props.industryOptions
 * @param {{ value: string, label: string }[]} props.frameworkOptions
 * @param {{ value: string, label: string }[]} props.kpiStatusOptions
 * @param {{ value: string, label: string }[]} props.categoryOptions
 * @param {string} [props.variablesError] a formula with no variables
 * @param {(rowId: string|number, field: string, value: *) => void} props.onField
 * @param {(rowId: string|number, field: string, value: string) => void} props.onToggleChip
 * @param {(rowId: string|number, action: object) => void} props.onUnitAction
 * @param {(rowId: string|number, action: object) => void} props.onFactorAction
 * @param {(rowId: string|number) => void} props.onToggleSelect
 * @param {(rowId: string|number) => void} props.onRemoveDraft
 * @param {(rowId: string|number, group: string, index: number, value: string) => void} props.onTagField
 * @param {(rowId: string|number, group: string, index: number, field: string, value: string) => void} props.onCreateTagValue
 * @param {(field: string, value: string, esgPillar: string|null) => void} props.onFileTagValue files a module tag created in its cell
 * @param {(moduleTag: string, esgPillar: string) => Promise<string|null>} props.onSetModulePillar
 * @param {(rowId: string|number, groups: object[]) => void} props.onOtherTags
 */
function KpiGridRow({
  columns,
  row,
  patch,
  isNew,
  selected,
  titleError,
  moduleError,
  aggregationError,
  unitsError,
  factorsError,
  detailsError,
  variablesError,
  aggregationDisabled,
  factorsLoading,
  factorsFailed,
  onRetryFactors,
  scopeOptions,
  countrySuggestions,
  ghgDatabases,
  moduleOptions,
  financialYearLabel,
  typeOptions,
  frequencyOptions,
  aggregationOptions,
  unitMaster,
  tagKeys,
  fixedTagGroups,
  tagOptions,
  tagValuesByKey,
  modulePillars,
  evidenceCatalogue,
  industryOptions,
  frameworkOptions,
  kpiStatusOptions,
  categoryOptions,
  onField,
  onToggleChip,
  onUnitAction,
  onFactorAction,
  onToggleSelect,
  onRemoveDraft,
  onTagField,
  onCreateTagValue,
  onFileTagValue,
  onSetModulePillar,
  onOtherTags,
}) {
  const values = { ...row, ...patch };
  const isDirty = (field) => !!patch && field in patch;
  const kpiTypeOptions = useMemo(() => kpiTypeOptionsFor(row.kpiTypes), [row.kpiTypes]);
  const unknownKpiTypes = values.kpiTypes.filter((type) => !isKnownKpiType(type));

  const handlers = useMemo(() => {
    const set = (field) => (value) => onField(row.id, field, value);
    return {
      title: set("title"),
      moduleId: set("moduleId"),
      type: set("type"),
      kpiTypes: set("kpiTypes"),
      frequency: set("frequency"),
      aggregation: set("aggregation"),
      active: set("active"),
      industries: set("industryIds"),
      frameworks: set("frameworkIds"),
      sectionMapping: set("sectionMapping"),
      formula: set("formula"),
      variables: set("variables"),
      kpiStatus: set("kpiStatus"),
      category: set("category"),
      defaultDescription: set("defaultDescription"),
      yearDescription: set("yearDescription"),
      details: set("details"),
      isReadOnly: set("isReadOnly"),
      variableUnits: set("variableUnits"),
      applicableCheck: set("applicableCheck"),
      applicableNote: set("applicableNote"),
      trigerCheck: set("trigerCheck"),
      otherTags: (groups) => onOtherTags(row.id, groups),
      evidences: (value) => onToggleChip(row.id, "evidences", value),
      units: (action) => onUnitAction(row.id, action),
      factors: (action) => onFactorAction(row.id, action),
      select: () => onToggleSelect(row.id),
      remove: () => onRemoveDraft(row.id),
    };
  }, [row.id, onField, onToggleChip, onUnitAction, onFactorAction, onToggleSelect, onRemoveDraft, onOtherTags]);

  const rowClass = [
    isNew ? "kgb-row--new" : "",
    !isNew && patch && Object.keys(patch).length ? "kgb-row--dirty" : "",
    titleError || moduleError || aggregationError || unitsError || factorsError || variablesError || detailsError
      ? "kgb-row--invalid"
      : "",
    selected ? "kgb-row--selected" : "",
    values.active ? "" : "kgb-row--inactive",
  ]
    .filter(Boolean)
    .join(" ");

  const otherTagsOf = (groups) => (groups || []).filter((group) => !fixedTagGroups.includes(group.name));
  const otherTags = otherTagsOf(values.tagGroups);
  const otherTagsDirty = JSON.stringify(otherTags) !== JSON.stringify(otherTagsOf(row.tagGroups));

  /** A fixed tag group: one cell per position, picked from that key's pairs; the module position also shows its ESG pillar. */
  const tagCells = (column) =>
    column.sub.map((field, index) => {
      const value = tagValue(values.tagGroups, column.tagGroup, index);
      const shared = {
        value,
        options: tagOptions[field.key] || EMPTY,
        dirty: value !== tagValue(row.tagGroups, column.tagGroup, index),
        ariaLabel: `${column.label}: ${field.label}`,
        onChange: (next) => onTagField(row.id, column.tagGroup, index, next),
      };
      return (
        <td key={field.key} className={index === 0 ? "kgb-group-start" : undefined}>
          {isModuleKeyword(field.key) ? (
            <ModuleTagCell
              {...shared}
              pillar={modulePillars[normalizeTagText(value)]}
              pillarOf={(moduleTag) => modulePillars[normalizeTagText(moduleTag)]}
              onFile={(next, esgPillar) => onFileTagValue(field.key, next, esgPillar)}
              onSetPillar={onSetModulePillar}
            />
          ) : (
            <TagValueCell
              {...shared}
              onCreate={(next) => onCreateTagValue(row.id, column.tagGroup, index, field.key, next)}
            />
          )}
        </td>
      );
    });

  /** One cell per column key, so hidden columns are simply not rendered. */
  const cells = {
    title: (
      <td className="kgb-col-title">
        <div className="kgb-titlecell">
          <TextCell
            value={values.title}
            dirty={isDirty("title")}
            error={titleError}
            strong
            ariaLabel="KPI title"
            placeholder="KPI title"
            onChange={handlers.title}
          />
          {isNew ? (
            <button
              type="button"
              className="kgb-row-remove"
              aria-label="Remove this new row"
              onClick={handlers.remove}
            >
              ×
            </button>
          ) : null}
        </div>
        {isNew ? <em className="kgb-newbadge">New</em> : null}
      </td>
    ),
    financialYear: (
      <td>
        <span className="kgb-readonly">{financialYearLabel || "—"}</span>
      </td>
    ),
    module: (
      <td>
        <SelectCell
          value={values.moduleId}
          options={moduleOptions}
          dirty={isDirty("moduleId")}
          error={moduleError}
          ariaLabel="Module"
          placeholder={values.moduleId ? undefined : "Choose a module"}
          onChange={handlers.moduleId}
        />
      </td>
    ),
    type: (
      <td>
        <SelectCell
          value={values.type}
          options={typeOptions}
          dirty={isDirty("type")}
          ariaLabel="Question type"
          onChange={handlers.type}
        />
      </td>
    ),
    kpiType: (
      <td>
        <MultiPickCell
          value={values.kpiTypes}
          options={kpiTypeOptions}
          dirty={isDirty("kpiTypes")}
          invalid={unknownKpiTypes.length > 0}
          ariaLabel="KPI type"
          placeholder="—"
          title={
            unknownKpiTypes.length
              ? `Saved as ${unknownKpiTypes.map((type) => `"${type}"`).join(", ")}, which company calculations do not match, so they are skipped for this KPI. Untick it, pick the type from the list and save.`
              : values.kpiTypes.map((type) => kpiTypeOptions.find((o) => o.value === type)?.label || type).join(", ")
          }
          noun="types"
          onChange={handlers.kpiTypes}
        />
      </td>
    ),
    frequency: (
      <td>
        <SelectCell
          value={values.frequency}
          options={frequencyOptions}
          dirty={isDirty("frequency")}
          ariaLabel="Reporting frequency"
          onChange={handlers.frequency}
        />
      </td>
    ),
    aggregation: (
      <td>
        <SelectCell
          value={aggregationDisabled ? "" : values.aggregation}
          options={aggregationOptions}
          dirty={isDirty("aggregation")}
          error={aggregationError}
          disabled={aggregationDisabled}
          ariaLabel="Aggregation method"
          placeholder={aggregationDisabled ? "Not applicable" : values.aggregation ? undefined : "Choose"}
          onChange={handlers.aggregation}
        />
      </td>
    ),
    category: (
      <td>
        <SelectCell
          value={values.category}
          options={categoryOptions}
          dirty={isDirty("category")}
          ariaLabel="Category"
          placeholder="—"
          onChange={handlers.category}
        />
      </td>
    ),
    defaultDescription: (
      <td>
        <TextCell
          value={values.defaultDescription}
          dirty={isDirty("defaultDescription")}
          ariaLabel="Default description, every financial year"
          placeholder="Shown for every financial year"
          onChange={handlers.defaultDescription}
        />
      </td>
    ),
    yearDescription: (
      <td>
        <TextCell
          value={values.yearDescription}
          dirty={isDirty("yearDescription")}
          ariaLabel="This year description"
          placeholder="Blank uses the default"
          onChange={handlers.yearDescription}
        />
      </td>
    ),
    tableSetup: (
      <td>
        <TableSetupCell
          type={values.type}
          details={values.details}
          title={values.title}
          dirty={isDirty("details")}
          error={detailsError}
          onChange={handlers.details}
        />
      </td>
    ),
    readOnly: (
      <td>
        <SwitchCell
          checked={values.isReadOnly}
          dirty={isDirty("isReadOnly")}
          disabled={values.type !== "tabular_question"}
          ariaLabel="Read only table"
          onChange={handlers.isReadOnly}
        />
      </td>
    ),
    variableUnits: (
      <td>
        <VariableUnitsCell
          variables={values.variables}
          value={values.variableUnits}
          unitMaster={unitMaster}
          dirty={isDirty("variableUnits")}
          onChange={handlers.variableUnits}
        />
      </td>
    ),
    applicableCheck: (
      <td>
        <SwitchCell
          checked={values.applicableCheck}
          dirty={isDirty("applicableCheck")}
          ariaLabel="Applicable check required"
          onChange={handlers.applicableCheck}
        />
      </td>
    ),
    applicableNote: (
      <td>
        <SwitchCell
          checked={values.applicableNote}
          dirty={isDirty("applicableNote")}
          ariaLabel="Applicable note required"
          onChange={handlers.applicableNote}
        />
      </td>
    ),
    trigerCheck: (
      <td>
        <SwitchCell
          checked={values.trigerCheck}
          dirty={isDirty("trigerCheck")}
          ariaLabel="Trigger check required"
          onChange={handlers.trigerCheck}
        />
      </td>
    ),
    emission: (
      <td className="kgb-col-emission">
        <EmissionFactorsCell
          factors={values.factors}
          loading={factorsLoading}
          failed={factorsFailed}
          onRetry={onRetryFactors}
          dirty={isDirty("factors")}
          error={factorsError}
          disabled={factorsLoading}
          scopeOptions={scopeOptions}
          countrySuggestions={countrySuggestions}
          unitMaster={unitMaster}
          databases={ghgDatabases}
          onAction={handlers.factors}
        />
      </td>
    ),
    units: (
      <td>
        <UnitsCell
          units={values.units}
          unitMaster={unitMaster}
          dirty={isDirty("units")}
          error={unitsError}
          onAction={handlers.units}
        />
      </td>
    ),
    tags: (
      <td>
        <TagGroupsCell
          groups={otherTags}
          shape={values.tagShape}
          tagKeys={tagKeys}
          tagValuesByKey={tagValuesByKey}
          dirty={otherTagsDirty}
          onChange={handlers.otherTags}
        />
      </td>
    ),
    evidences: (
      <td>
        <ChipsCell
          values={values.evidences}
          catalogue={evidenceCatalogue}
          dirty={isDirty("evidences")}
          label="Required evidence"
          creatable
          onToggle={handlers.evidences}
        />
      </td>
    ),
    industries: (
      <td>
        <MultiPickCell
          value={values.industryIds}
          options={industryOptions}
          dirty={isDirty("industryIds")}
          ariaLabel="Industries"
          allLabel="All industries"
          noun="industries"
          onChange={handlers.industries}
        />
      </td>
    ),
    frameworks: (
      <td>
        <MultiPickCell
          value={values.frameworkIds}
          options={frameworkOptions}
          dirty={isDirty("frameworkIds")}
          ariaLabel="Frameworks"
          allLabel="No frameworks"
          noun="frameworks"
          onChange={handlers.frameworks}
        />
      </td>
    ),
    sectionMapping: (
      <td>
        <TextCell
          value={values.sectionMapping}
          dirty={isDirty("sectionMapping")}
          ariaLabel="Framework section mapping"
          placeholder="—"
          onChange={handlers.sectionMapping}
        />
      </td>
    ),
    formula: (
      <td>
        <TextCell
          value={values.formula}
          dirty={isDirty("formula")}
          ariaLabel="Formula"
          placeholder="{{Q594}}+{{Q595}}"
          onChange={handlers.formula}
        />
      </td>
    ),
    variables: (
      <td>
        <ListCell
          value={values.variables}
          dirty={isDirty("variables")}
          error={variablesError}
          ariaLabel="Formula variables"
          placeholder="Q594; Q595"
          onChange={handlers.variables}
        />
      </td>
    ),
    kpiStatus: (
      <td>
        <SelectCell
          value={values.kpiStatus}
          options={kpiStatusOptions}
          dirty={isDirty("kpiStatus")}
          ariaLabel="KPI status"
          onChange={handlers.kpiStatus}
        />
      </td>
    ),
    active: (
      <td>
        <SwitchCell
          checked={values.active}
          dirty={isDirty("active")}
          ariaLabel="Active"
          onChange={handlers.active}
        />
      </td>
    ),
  };

  return (
    <tr className={rowClass}>
      <td className="kgb-col-select">
        <input
          type="checkbox"
          className="kgb-checkbox"
          checked={selected}
          onChange={handlers.select}
          aria-label={`Select ${values.title || "new KPI"}`}
        />
      </td>

      {columns.map((column) => (
        <Fragment key={column.key}>{column.sub ? tagCells(column) : cells[column.key]}</Fragment>
      ))}
    </tr>
  );
}

export default memo(KpiGridRow);
